/**
 * Centralized logger with Application Insights integration.
 *
 * Replaces direct console.log usage with structured logging that:
 * - Adds timestamps and log levels
 * - Sends logs to Application Insights (except debug)
 * - Respects configurable log level via NEXT_PUBLIC_LOG_LEVEL
 *
 * Usage:
 *   import { logger } from '@/src/lib/logger'
 *   logger.info('User logged in', { userId: '123' })
 *   logger.error('Failed to fetch data', error, { endpoint: '/api/users' })
 */

import { trackException, trackEvent } from './appInsights'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  [key: string]: string | number | boolean | undefined
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

// Get minimum log level from environment
const MIN_LOG_LEVEL: LogLevel =
  (process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel) ||
  (process.env.NODE_ENV === 'production' ? 'warn' : 'debug')

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LOG_LEVEL]
}

function formatMessage(level: LogLevel, message: string, context?: LogContext): string {
  const timestamp = new Date().toISOString()
  const contextStr = context ? ` ${JSON.stringify(context)}` : ''
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`
}

function sendToAppInsights(level: LogLevel, message: string, context?: LogContext) {
  const properties: Record<string, string> = {
    logLevel: level,
  }

  // Add context properties as strings
  if (context) {
    for (const [key, value] of Object.entries(context)) {
      if (value !== undefined) {
        properties[key] = String(value)
      }
    }
  }

  // Add page info if in browser
  if (typeof window !== 'undefined') {
    properties.page = window.location.pathname
  }

  if (level === 'error') {
    trackException(new Error(message), properties)
  } else {
    trackEvent(`log_${level}`, { message, ...properties })
  }
}

export const logger = {
  /**
   * Debug level logging - for development only.
   * NOT sent to Application Insights.
   */
  debug(message: string, context?: LogContext) {
    if (!shouldLog('debug')) return
    console.debug(formatMessage('debug', message, context))
    // Don't send debug to App Insights
  },

  /**
   * Info level logging - for general operational messages.
   * Sent to Application Insights in production.
   */
  info(message: string, context?: LogContext) {
    if (!shouldLog('info')) return
    console.info(formatMessage('info', message, context))
    sendToAppInsights('info', message, context)
  },

  /**
   * Warning level logging - for potentially problematic situations.
   * Sent to Application Insights.
   */
  warn(message: string, context?: LogContext) {
    if (!shouldLog('warn')) return
    console.warn(formatMessage('warn', message, context))
    sendToAppInsights('warn', message, context)
  },

  /**
   * Error level logging - for errors and exceptions.
   * Always sent to Application Insights.
   */
  error(message: string, error?: Error, context?: LogContext) {
    if (!shouldLog('error')) return
    const fullContext = {
      ...context,
      errorMessage: error?.message,
      errorStack: error?.stack?.slice(0, 500),
    }
    console.error(formatMessage('error', message, fullContext))
    sendToAppInsights('error', message, fullContext)
  },
}

export default logger
